import { v4 } from 'uuid';
import { inspectionDB } from '../data/database.structure';
import { observationDraftService } from './observationDraftService';
import { inspectionRecordService } from './inspection/inspectionRecordService';
import ObservationHelpers from '../helpers/observationHelpers';
import { InspectionCategoryTypeModel, ObservationModel } from '../data/types';
import {
  DataTypesEnum,
  getEnumKeyByValue,
  ObservableLevelEnumKey,
  ObservableLevelEnumValue,
  ObservableLevelsEnum,
} from '../data/enums';

export interface ObservationUpsertProps {
  inspectionId: string;
  inspectionCategoryId: string;
  entityId: number;
  entityExternalId: string;
  dataLevel: ObservableLevelEnumValue;
  path: string;
  value: string;
  unitId?: number;
  unitName?: string;
  actionTypeId?: number;
  actionTypeName?: string;
  isSynced?: boolean;
}

const dataLevelNameOf = (dataLevel: ObservableLevelEnumValue) =>
  getEnumKeyByValue(
    Object.fromEntries(
      Object.entries(ObservableLevelsEnum).map((entry) => [
        entry[0],
        entry[1] as number,
      ]),
    ),
    dataLevel,
  ) as string;

class ObservationService {
  constructor() {}

  async upsertObservation({
    inspectionId,
    inspectionCategoryId,
    entityId,
    entityExternalId,
    dataLevel,
    path,
    value,
    unitId = -1,
    unitName,
    actionTypeId = -1,
    actionTypeName,
    isSynced = false,
  }: ObservationUpsertProps): Promise<string> {
    try {
      // Captured BEFORE the write: drafts the user touches after this moment are
      // newer than this write and must not be superseded by it.
      const stampBeforeWrite = observationDraftService.currentStamp();
      const dataLevelName = dataLevelNameOf(dataLevel);
      if (!dataLevelName) {
        return '';
      }

      const pathModified = path.replace('ObservableItemIdentifier.', '');
      let writtenExternalId = '';
      // The transaction opens before anything is awaited: a draft flush started after
      // this call must commit after it, or this older value lands over the newer draft.
      const stored = await inspectionDB.transaction(
        'rw',
        [
          inspectionDB.inspections,
          inspectionDB.inspectionBackups,
          inspectionDB.observableItems,
        ],
        async () => {
          const observableItem = await inspectionDB.observableItems
            .where('identifier')
            .equals(pathModified)
            .first();
          if (!observableItem) {
            throw new Error('observationService.observableItem.empty');
          }

          return inspectionRecordService.change(inspectionId, (inspection) => {
            const inspectionCategoryType =
              inspection.inspectionCategoryTypes.find(
                (category) => category.externalId === inspectionCategoryId,
              );
            if (inspectionCategoryType === undefined) {
              throw new Error(
                'observationService.inspectionCategoryTypeFound.empty',
              );
            }
            const observations = inspectionCategoryType.observations;

            if (observableItem.isCollection) {
              // Always insert if the ObservableItem is a collection
              writtenExternalId = v4();
              observations.push({
                id: 0,
                externalId: writtenExternalId,
                value,
                entityId,
                entityExternalId,
                dataLevelId: dataLevel,
                categoryTypeId: inspectionCategoryType.id,
                unitId: unitId,
                observableItemId: observableItem.id,
                isSynced: isSynced,
                actionTypeId,
                dataLevel: dataLevelName, // Add appropriate value
                unit: unitName ?? null, // Add appropriate value
                actionType: actionTypeName ?? '', // Add appropriate value
                observableItem,
                fileReceived: false,
              });
              return;
            }

            // Check if observation exists
            const currentObservable = observations.find(
              (element) =>
                element.observableItemId === observableItem.id &&
                ObservationHelpers.isObservationForEntity({
                  observation: element,
                  dataLevel,
                  entityId,
                  entityExternalId,
                }),
            );

            if (currentObservable) {
              // Replaced where it stands rather than moved to the end: the push keeps the
              // FIRST row per field (sanitizeObservationsForWire), so moving the edited row
              // behind an older duplicate - as a re-home can leave behind (#11006) - would
              // ship the stale twin instead of the value just entered.
              const observationIndex = observations.indexOf(currentObservable);
              observations[observationIndex] = {
                id: currentObservable.id,
                externalId: currentObservable.externalId || v4(),
                value,
                // The row's own part id, which differs from the caller's for a reading
                // taken before its bucket was re-homed (#11006).
                entityId: currentObservable.entityId,
                entityExternalId,
                dataLevelId: dataLevel,
                categoryTypeId: inspectionCategoryType.id ?? -1,
                // A caller that doesn't know the unit must not STOMP the unit already
                // recorded: rewriting an existing measurement's unitId to -1 detached it
                // from every unit-aware matcher (tombstones could no longer delete it)
                // and is how mixed-unit observables are born (pack-1 model hunt find;
                // the RT-E audit found one such row in production data).
                unitId: unitId === -1 ? currentObservable.unitId : unitId,
                observableItemId: observableItem.id,
                isSynced: false,
                actionTypeId,
                dataLevel: dataLevelName, // Add appropriate value
                unit: unitName ?? null, // Add appropriate value
                actionType: actionTypeName ?? '', // Add appropriate value
                observableItem,
                fileReceived: false,
              };
              writtenExternalId = observations[observationIndex].externalId;
              return;
            }

            // Insert new observation
            writtenExternalId = v4();
            observations.push({
              id: 0,
              externalId: writtenExternalId,
              value,
              entityId,
              entityExternalId,
              dataLevelId: dataLevel,
              categoryTypeId: inspection.inspectionCategoryTypes[0].id,
              unitId: unitId,
              observableItemId: observableItem.id,
              isSynced: false,
              actionTypeId,
              dataLevel: dataLevelName, // Add appropriate value
              unit: unitName ?? null, // Add appropriate value
              actionType: actionTypeName ?? '', // Add appropriate value
              observableItem,
              fileReceived: false,
            });
          });
        },
      );
      if (!stored) {
        throw new Error('observationService.inspection.empty');
      }
      if (writtenExternalId !== '') {
        // A pending draft for this field is now stale - without this its later
        // flush clobbers the value just written (a stale draft TOMBSTONE even
        // deletes it; pack-1 model hunt find).
        observationDraftService.supersede(
          {
            inspectionId,
            inspectionCategoryId,
            dataLevel,
            entityId,
            entityExternalId,
            path,
            unitId,
            unitName,
          },
          value,
          stampBeforeWrite,
        );
      }
      return writtenExternalId;
    } catch (error) {
      console.error('Error in upsertObservation:', error);
      return '';
    }
  }

  entityObservationsHasAnyPaths({
    elements,
    requiredPaths,
    pathsStartingWith,
    level,
    entityId,
    entityExternalId,
  }: {
    elements: InspectionCategoryTypeModel[];
    requiredPaths: string[];
    pathsStartingWith: string[];
    level: ObservableLevelsEnum;
    entityId: number;
    entityExternalId: string;
  }) {
    if (entityId === 0) {
      return false;
    }

    const relatedObservations: ObservationModel[] = [];

    if (pathsStartingWith.length) {
      elements.forEach((e) =>
        e.observations.forEach((o) => {
          if (
            pathsStartingWith.some((p) =>
              o.observableItem.identifier.startsWith(p),
            )
          ) {
            relatedObservations.push(o);
          }
        }),
      );
      const result = relatedObservations.filter(
        (o) =>
          ObservationHelpers.isObservationForEntity({
            observation: o,
            dataLevel: level,
            entityId,
            entityExternalId,
          }) &&
          o.value !== '' &&
          o.value !== 'false',
      ).length;

      if (result > 0) {
        return true;
      }
    }
    elements.forEach((e) =>
      e.observations.forEach((o) => {
        if (
          requiredPaths.some((p) => o.observableItem.identifier.startsWith(p))
        ) {
          relatedObservations.push(o);
        }
      }),
    );
    const requiredCompleted = relatedObservations.filter(
      (o) =>
        ObservationHelpers.isObservationForEntity({
          observation: o,
          dataLevel: level,
          entityId,
          entityExternalId,
        }) &&
        o.value !== '' &&
        o.value !== 'false',
    ).length;

    return requiredCompleted > 0;
  }

  entityObservationsCompletePercentageByPathsLike(
    elements: InspectionCategoryTypeModel[],
    requiredPaths: string[],
    level: ObservableLevelsEnum,
    entityId: number,
    entityExternalId: string,
  ): number {
    if (entityId === 0) {
      return 0;
    }
    const relatedObservations: ObservationModel[] = [];

    elements.forEach((e) =>
      e.observations.forEach((o) => {
        if (requiredPaths.includes(o.observableItem.identifier)) {
          relatedObservations.push(o);
        }
      }),
    );

    const requiredCompleted = relatedObservations.filter(
      (o) =>
        ObservationHelpers.isObservationForEntity({
          observation: o,
          dataLevel: level,
          entityId,
          entityExternalId,
        }) &&
        o.value !== '' &&
        o.value !== 'false',
    ).length;

    if (requiredCompleted > 0) {
      if (requiredPaths.length === requiredCompleted) {
        return 100;
      }
      return Math.round((requiredCompleted / requiredPaths.length) * 100);
    }
    return 0;
  }

  async deleteObservationByPath({
    inspectionId,
    inspectionCategoryId,
    path,
  }: {
    inspectionId: string;
    inspectionCategoryId: string;
    path: string;
  }): Promise<boolean> {
    let deleted = false;
    await inspectionRecordService.change(inspectionId, (inspection) => {
      const inspectionCategoryType = inspection.inspectionCategoryTypes.find(
        (category) => category.externalId === inspectionCategoryId,
      );
      if (!inspectionCategoryType) {
        return;
      }
      inspectionCategoryType.observations =
        inspectionCategoryType.observations.filter(
          (observation) => observation.observableItem.identifier !== path,
        );
      deleted = true;
    });
    return deleted;
  }

  async deleteObservationsByProps({
    inspectionId,
    inspectionCategoryId,
    entityId,
    entityExternalId,
    dataLevel,
    path,
    unitId = -1,
  }: {
    inspectionId: string;
    inspectionCategoryId: string;
    entityId: number;
    entityExternalId: string;
    dataLevel: ObservableLevelEnumValue;
    path: string;
    unitId?: number;
  }): Promise<boolean> {
    try {
      // Captured BEFORE the delete: drafts typed after this moment are newer.
      const stampBeforeDelete = observationDraftService.currentStamp();
      if (!dataLevelNameOf(dataLevel)) {
        return false;
      }

      let deleted = false;
      // The transaction opens before anything is awaited: a draft flush started after
      // this call must commit after it, or this delete removes the newer draft.
      await inspectionDB.transaction(
        'rw',
        [
          inspectionDB.inspections,
          inspectionDB.inspectionBackups,
          inspectionDB.observableItems,
        ],
        async () => {
          const observableItem = await inspectionDB.observableItems
            .where('identifier')
            .equals(path)
            .first();
          if (!observableItem) {
            return;
          }

          await inspectionRecordService.change(inspectionId, (inspection) => {
            const inspectionCategoryType =
              inspection.inspectionCategoryTypes.find(
                (category) => category.externalId === inspectionCategoryId,
              );
            if (!inspectionCategoryType) {
              return;
            }
            // For NON-collections the unit is metadata, not identity (HUNT-5): a caller
            // that doesn't know the recorded unit must still delete the field's row -
            // unit-strict matching left unit-mismatched rows alive while supersede() told
            // the draft layer the field was deleted, so the value resurrected on reload.
            // Collections (e.g. pitch-gauge readings) keep unit-strict matching - their
            // callers delete by exact unit deliberately.
            inspectionCategoryType.observations =
              inspectionCategoryType.observations.filter(
                (element) =>
                  !(
                    element.observableItemId === observableItem.id &&
                    ObservationHelpers.isObservationForEntity({
                      observation: element,
                      dataLevel,
                      entityId,
                      entityExternalId,
                    }) &&
                    (observableItem.isCollection
                      ? element.unitId === unitId
                      : true)
                  ),
              );
            deleted = true;
          });
        },
      );
      if (deleted) {
        // AFTER the transaction commits (a superseded draft with an aborted delete
        // would leave the rows undeleted but the tombstone forgotten): a pending
        // draft would otherwise resurrect the deleted observation on its next
        // flush - unless the user typed after this delete began; their draft wins.
        observationDraftService.supersede(
          {
            inspectionId,
            inspectionCategoryId,
            dataLevel,
            entityId,
            entityExternalId,
            path,
            unitId,
          },
          null,
          stampBeforeDelete,
        );
      }
      return deleted;
    } catch (error) {
      console.error('Error in deleteObservationsByProps:', error);
      return false;
    }
  }

  async getObservation<T>({
    inspectionId,
    inspectionCategoryId,
    entityId,
    entityExternalId,
    dataLevel,
    path,
  }: {
    entityId: number;
    entityExternalId: string;
    dataLevel: ObservableLevelEnumValue;
    path: string;
    inspectionId: string;
    inspectionCategoryId: string;
  }): Promise<ObservationModel | undefined> {
    const inspection = await inspectionDB.inspections
      .where('externalId')
      .equals(inspectionId)
      .first();

    if (!inspection) {
      return undefined;
    }

    let observations: ObservationModel[] = [];

    for (let i = 0; i < inspection.inspectionCategoryTypes.length; i++) {
      if (
        inspection.inspectionCategoryTypes[i].externalId ===
        inspectionCategoryId
      ) {
        observations = inspection.inspectionCategoryTypes[i].observations;
        break;
      }
    }

    const pathModified = path.replace('ObservableItemIdentifier.', '');

    const observation = observations.find(
      (ob) =>
        ObservationHelpers.isObservationForEntity({
          observation: ob,
          dataLevel,
          entityId,
          entityExternalId,
        }) && ob.observableItem.identifier === pathModified,
    );

    if (!observation) {
      return undefined;
    }

    const convertedType = this.convertValue<T>(
      observation.value,
      observation.observableItem.dataTypeId,
    );

    if (convertedType === undefined) {
      return undefined;
    }

    return observation;
  }

  async getObservationsByInspection<T>({
    inspectionId,
    inspectionCategoryId,
    entityId,
    entityExternalId,
    dataLevel,
    path,
  }: {
    entityId: number;
    entityExternalId: string;
    dataLevel: ObservableLevelEnumValue;
    path: string;
    inspectionId: string;
    inspectionCategoryId: string;
  }): Promise<ObservationModel[]> {
    const inspection = await inspectionDB.inspections
      .where('externalId')
      .equals(inspectionId)
      .first();

    if (!inspection) {
      return [];
    }

    let observations: ObservationModel[] = [];

    for (let i = 0; i < inspection.inspectionCategoryTypes.length; i++) {
      if (
        inspection.inspectionCategoryTypes[i].externalId ===
        inspectionCategoryId
      ) {
        observations = inspection.inspectionCategoryTypes[i].observations;
        break;
      }
    }

    observations = observations.filter(
      (ob) =>
        ObservationHelpers.isObservationForEntity({
          observation: ob,
          dataLevel,
          entityId,
          entityExternalId,
        }) && ob.observableItem.identifier === path,
    );

    if (!observations) {
      return [];
    }

    // Check if all observations are of the same data type
    for (let i = 0; i < observations.length; i++) {
      const observation = observations[i];
      const convertedType = this.convertValue<T>(
        observation.value,
        observation.observableItem.dataTypeId,
      );
      if (convertedType === undefined) {
        return [];
      }
    }

    return observations;
  }

  getObservations<T>({
    entityId,
    entityExternalId,
    dataLevel,
    path,
    observations,
  }: {
    entityId: number;
    entityExternalId: string;
    dataLevel: ObservableLevelEnumValue;
    path: string;
    observations: ObservationModel[];
  }): ObservationModel[] {
    const observationList = observations.filter(
      (ob) =>
        ObservationHelpers.isObservationForEntity({
          observation: ob,
          dataLevel,
          entityId,
          entityExternalId,
        }) && ob.observableItem.identifier === path,
    );

    if (observationList.length === 0) {
      return [];
    }

    // Check if all observations are collections
    if (
      observationList.filter((ob) => !ob.observableItem.isCollection).length > 0
    ) {
      return [];
    }

    // Check if all observations are of the same data type
    for (let i = 0; i < observationList.length; i++) {
      const observation = observationList[i];
      const convertedType = this.convertValue<T>(
        observation.value,
        observation.observableItem.dataTypeId,
      );
      if (convertedType === undefined) {
        return [];
      }
    }

    return observationList;
  }

  // Delete observation by externalId. An observation that is already gone is a
  // no-op, but a failed write or aborted transaction rejects: swallowing it told
  // callers a photo reference was removed while it was still stored (#11481).
  async deleteObservation(
    inspectionId: string,
    inspectionCategoryTypeId: string,
    observationId: string,
  ): Promise<void> {
    await inspectionRecordService.change(inspectionId, (inspection) => {
      const inspectionCategoryType = inspection.inspectionCategoryTypes.find(
        (category) => category.externalId === inspectionCategoryTypeId,
      );
      if (!inspectionCategoryType) {
        return;
      }
      const observationIndex = inspectionCategoryType.observations.findIndex(
        (ob) => ob.externalId === observationId,
      );
      if (observationIndex !== -1) {
        inspectionCategoryType.observations.splice(observationIndex, 1);
      }
    });
  }

  convertValue<T>(value: string, dataType: DataTypesEnum): T {
    switch (dataType) {
      case DataTypesEnum.string:
        return value as T;
      case DataTypesEnum.number:
        return Number(value) as T;
      case DataTypesEnum.date:
        const parsed = new Date(value);

        const reconstructed = new Date(
          Date.UTC(
            parsed.getFullYear(),
            parsed.getMonth(),
            parsed.getDate(),
            parsed.getHours(),
            parsed.getMinutes(),
            parsed.getSeconds(),
            parsed.getMilliseconds(),
          ),
        );
        return reconstructed as T;
      case DataTypesEnum.image:
        return value as T;
      case DataTypesEnum.boolean:
        // Handle various string representations of boolean
        const normalizedValue = value.toLowerCase().trim();
        return (normalizedValue === 'true' || normalizedValue === '1') as T;
      default:
        throw new Error(`Unsupported data type: ${dataType}`);
    }
  }
}

export const observationService = new ObservationService();
